import type { CompanyApplication } from "../../../domain/types/shared";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  ApiResponseUserInfoResponse,
  ApiResponseCompanyAdminCompanyInfoResponse,
  ApiResponseCompanyInfoResponse,
  ApiResponseBoolean,
  UpdateCompanyInfoRequest,
  UpdateUserRoleRequest,
  WithdrawCompanyUserRequest,
  SearchCompanyUsersRequest,
  ApiResponseSearchCompanyUsersResponse,
  ApiResponseRealtimeGraphVertex,
} from "../../../shared/types/api";

export class CompanyApiService {
  async getCompanyInfo(
    companyId: number
  ): Promise<ApiResponseCompanyInfoResponse> {
    const response = await httpClient.get<ApiResponseCompanyInfoResponse>(
      NEW_API_ENDPOINTS.companyController.GET.byId(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "업체 정보를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async updateCompanyInfo(
    request: UpdateCompanyInfoRequest
  ): Promise<ApiResponseCompanyAdminCompanyInfoResponse> {
    const formData = new FormData();

    formData.append("companyId", request.companyId.toString());
    formData.append("companyAdminId", request.companyAdminId.toString());
    formData.append("companyName", request.companyName);

    if (request.ceoName) formData.append("ceoName", request.ceoName);
    if (request.domain) formData.append("domain", request.domain);
    // boolean 은 false 도 저장해야 하므로 truthy 검사가 아니라 undefined 검사
    if (request.searchEngineIndexingEnabled !== undefined) {
      formData.append(
        "searchEngineIndexingEnabled",
        String(request.searchEngineIndexingEnabled),
      );
    }
    if (request.phone) formData.append("phone", request.phone);
    if (request.secondaryContactType) {
    formData.append("secondaryContactType", request.secondaryContactType);
    }
    if (request.secondaryContact) {
      formData.append("secondaryContact", request.secondaryContact);
    }
    if (request.zipCode) formData.append("zipCode", request.zipCode);
    if (request.address) formData.append("address", request.address);
    if (request.addressDetail)
      formData.append("addressDetail", request.addressDetail);
    if (request.businessNumber)
      formData.append("businessNumber", request.businessNumber);
    if (request.businessType)
      formData.append("businessType", request.businessType);
    if (request.businessCategory)
      formData.append("businessCategory", request.businessCategory);
    if (request.homeTaxKey) formData.append("homeTaxKey", request.homeTaxKey);
    if (request.tossPayKey) formData.append("tossPayKey", request.tossPayKey);
    if (request.channelTalkKey)
      formData.append("channelTalkKey", request.channelTalkKey);

    // 파일 필드 추가
    if (request.logoFile) {
      formData.append("logoFile", request.logoFile);
    }
    if (request.faviconFile) {
      formData.append("faviconFile", request.faviconFile);
    }
    if (request.businessRegistrationFile) {
      formData.append(
        "businessRegistrationFile",
        request.businessRegistrationFile
      );
    }
    if (request.companySealImageFile) {
      formData.append("companySealImageFile", request.companySealImageFile);
    }

    if (request.ecommerceBizNumber) {
      formData.append("ecommerceBizNumber", request.ecommerceBizNumber);
    }

    if (request.businessHours) {
      formData.append("businessHours", request.businessHours);
    }
    if (request.lunchBreak) {
      formData.append("lunchBreak", request.lunchBreak);
    }
    if (request.businessHoursNotice) {
      formData.append("businessHoursNotice", request.businessHoursNotice);
    }
    // 빈 문자열이면 전송하지 않아 백엔드에서 null 로 비워진다(푸터 줄 삭제).
    if (request.additionalBusinessInfo) {
      formData.append("additionalBusinessInfo", request.additionalBusinessInfo);
    }
    const response =
      await httpClient.put<ApiResponseCompanyAdminCompanyInfoResponse>(
        NEW_API_ENDPOINTS.companyController.PUT.update(request.companyId),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "회사 정보 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async updateUserRole(
    request: UpdateUserRoleRequest
  ): Promise<ApiResponseBoolean> {
    const response = await httpClient.put<ApiResponseBoolean>(
      NEW_API_ENDPOINTS.companyController.PUT.updateUserRole(request.companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "유저 역할 수정에 실패하였습니다."
      );
    }

    return response.data;
  }

  /** 회사 소속 쇼핑몰 회원 탈퇴 처리 (COMPANY_ADMIN 전용, 되돌릴 수 없음) */
  async withdrawCompanyUser(
    request: WithdrawCompanyUserRequest
  ): Promise<ApiResponseBoolean> {
    const response = await httpClient.delete<ApiResponseBoolean>(
      NEW_API_ENDPOINTS.companyController.DELETE.withdrawUser(
        request.companyId,
        request.userId
      )
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "회원 탈퇴 처리에 실패하였습니다."
      );
    }

    return response.data;
  }

  async requestCompany(application: CompanyApplication): Promise<ApiResponseBoolean> {
    const response = await httpClient.put<ApiResponseBoolean>(
      NEW_API_ENDPOINTS.companyController.PUT.request,
      application
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "업체 신청에 실패하였습니다.");
    }

    return response.data;
  }

  async getUserByCompanyId(
    request: SearchCompanyUsersRequest
  ): Promise<ApiResponseSearchCompanyUsersResponse> {
    const response =
      await httpClient.get<ApiResponseSearchCompanyUsersResponse>(
        NEW_API_ENDPOINTS.companyController.GET.users(request.companyId),
        {
          params: request.pageable,
        }
      );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg ||
          "해당 업체 아이디와 일치하는 업체를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async getCompanyRealtimeGraph(
    companyId: number
  ): Promise<ApiResponseRealtimeGraphVertex> {
    const response = await httpClient.get<ApiResponseRealtimeGraphVertex>(
      NEW_API_ENDPOINTS.companyController.GET.realtimeGraph(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "실시간 그래프를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async getCompanyAdminInfo(
    companyId: number
  ): Promise<ApiResponseUserInfoResponse> {
    const response = await httpClient.get<ApiResponseUserInfoResponse>(
      NEW_API_ENDPOINTS.companyController.GET.adminInfo(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "업체 어드민 정보를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  /**
   * 회사 이메일 설정 조회
   */
  async getEmailSettings(companyId: number): Promise<{
    data: { senderEmail: string | null; hasPassword: boolean };
  }> {
    const response = await httpClient.get<{
      data: { senderEmail: string | null; hasPassword: boolean };
      errorCode?: number;
      errorMsg?: string;
    }>(`/api/company/${companyId}/email-settings`);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "이메일 설정을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  /**
   * 회사 이메일 설정 수정
   */
  async updateEmailSettings(
    companyId: number,
    senderEmail: string,
    senderEmailPassword: string
  ): Promise<{
    data: { senderEmail: string | null; hasPassword: boolean };
  }> {
    const response = await httpClient.put<{
      data: { senderEmail: string | null; hasPassword: boolean };
      errorCode?: number;
      errorMsg?: string;
    }>(`/api/company/${companyId}/email-settings`, {
      senderEmail,
      senderEmailPassword,
    });

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "이메일 설정 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  /**
   * 토스페이먼츠 Secret Key 업데이트
   */
  async updateTossSecretKey(
    companyId: number,
    tossSecretKey: string
  ): Promise<ApiResponseBoolean> {
    const response = await httpClient.put<ApiResponseBoolean>(
      `/api/company/${companyId}/toss-secret-key`,
      {
        tossSecretKey,
      }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "토스페이먼츠 Secret Key 설정에 실패했습니다."
      );
    }

    return response.data;
  }
}

export const adminApiService = new CompanyApiService();
